/**
 * Vocabulário ÚNICO dos três contadores de canal. Zero React de propósito — os
 * ícones ficam com quem renderiza.
 *
 * Ícone + tooltip existe porque **rótulo em texto foi o que produziu a semântica
 * falsa da tela antiga**: "aguardando" era `total_unread_messages` (MENSAGENS) e
 * "em atendimento" era `chats_count` (`created` + `active` juntos). Nenhum dos três
 * números particiona nada — o mesmo protocolo entra em `chats_count` E em
 * `waiting_chats`. Ícone obriga o tooltip a dizer o que o número é.
 */
export const COUNTER_TOOLTIPS = {
  chats_count: 'Conversas abertas',
  waiting_chats: 'Conversas aguardando resposta',
  total_unread_messages: 'Mensagens não lidas',
} as const

export type CounterKey = keyof typeof COUNTER_TOOLTIPS

export interface ChannelCounterValues {
  chats_count: number
  waiting_chats: number
  total_unread_messages: number
}

/** Rótulo acessível de UM contador. O tooltip é só pro olho: `aria-label` com o texto
 *  do tooltip esconderia o número do leitor de tela. */
export function counterAriaLabel(key: CounterKey, value: number): string {
  return `${value} ${COUNTER_TOOLTIPS[key].toLowerCase()}`
}

/** Cap do badge: acima de 99 o número estoura o círculo de `h-4 min-w-4`.
 *  Mesmo corte do `NotificationBell`. */
export function unreadBadgeLabel(total: number): string {
  return total > 99 ? '99+' : String(total)
}

/** Os três por extenso, para a superfície que pode gastar palavra (o hover da linha
 *  de canal no `BrandMenu`). Contador zerado é omitido; tudo zerado vira uma palavra
 *  em vez de três zeros empilhados. */
export function describeChannelCounters(counters: ChannelCounterValues): string {
  const parts: string[] = []

  if (counters.chats_count > 0) {
    parts.push(`${counters.chats_count} ${counters.chats_count === 1 ? 'conversa aberta' : 'conversas abertas'}`)
  }
  if (counters.waiting_chats > 0) {
    parts.push(`${counters.waiting_chats} aguardando nossa resposta`)
  }
  if (counters.total_unread_messages > 0) {
    parts.push(
      `${counters.total_unread_messages} ${counters.total_unread_messages === 1 ? 'mensagem não lida' : 'mensagens não lidas'}`,
    )
  }

  return parts.length === 0 ? 'Sem conversas' : parts.join(' · ')
}
